import React, { createContext, useContext, useState } from "react";
import { MobileAppVersionListItem, MobileAppVersionDetail, OperationStatus, MobileAppVersionData, defaultMobileAppVersionData } from "./constants";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { MobileAppVersionService } from "../../services/mobileAppVersion";
import { useMemo, useCallback } from "react";
import { getErrorMessage } from "../../utils/errorUtils";

interface MobileAppVersionContextState {
	// Core Domain Data
	versionList: MobileAppVersionListItem[];
	versionDetail: MobileAppVersionDetail | null;
	versionData: MobileAppVersionData;
	operationStatus: OperationStatus;

	// Setters
	setVersionData: React.Dispatch<React.SetStateAction<MobileAppVersionData>>;
	clearMobileAppVersionData: () => void;
	clearMobileAppVersionList: () => void;

	// Actions
	addMobileAppVersion: () => Promise<void>;
    getMobileAppVersionList: () => Promise<void>;
	updateMobileAppVersion: () => Promise<void>;
	getMobileAppVersionDetails: (id: string) => Promise<void>;
    removeMobileAppVersion: (id: string) => Promise<void>;
}

interface MobileAppVersionProviderProps {
	children: React.ReactNode;
}

const MobileAppVersionContext = createContext<MobileAppVersionContextState | undefined>(
	undefined
);

export const useMobileAppVersionContext = () => {
	const context = useContext(MobileAppVersionContext);
	if (!context) {
		throw new Error(
			"useMobileAppVersionContext must be used within a MobileAppVersionContextProvider"
		);
	}
	return context;
};

export const MobileAppVersionContextProvider: React.FC<MobileAppVersionProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [versionList, setVersionList] = useState<MobileAppVersionListItem[]>([]);
	const [versionDetail, setVersionDetail] = useState<MobileAppVersionDetail | null>(null);
	const [versionData, setVersionData] = useState<MobileAppVersionData>(defaultMobileAppVersionData);

	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });
	const COMPLETE_VERSION_REGEX = /^\d+\.\d+\.\d+$/;

	const isVersionValid = (version: string): boolean => {
		return COMPLETE_VERSION_REGEX.test(version);
	};

	const isMobileAppVersionDataValid = (data: MobileAppVersionData) => {
		if (!data.platform || !data.version || data.forceUpdate === undefined || !data.appId) {
			return false;
		}

		return true;
	};

	const clearMobileAppVersionData = () => {
		setVersionData(defaultMobileAppVersionData);
		setVersionDetail(null);
	};

	const clearMobileAppVersionList = () => {
		setVersionList([]);
	};

	const addMobileAppVersion = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isMobileAppVersionDataValid(versionData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		if (!isVersionValid(versionData.version)) {
			message.info("Lütfen geçerli bir versiyon giriniz. (Örn: 1.0.0)");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await MobileAppVersionService.addMobileAppVersion(versionData);

			if (response.isSuccessful) {
				message.success("Mobil Uygulama Sürümü başarıyla eklendi.");
				setVersionData(defaultMobileAppVersionData);
				navigate("/mobile-app-versions");
			} else {
				message.error(response.error?.message || "Beklenmedik Bir Hata oluştu.");
			}

		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [versionData, navigate]);

	const updateMobileAppVersion = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!versionData.versionId) {
			message.info("Mobil Uygulama Sürümü numarası bulunamadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const isForceUpdateChanged = versionData.forceUpdate !== undefined && versionData.forceUpdate !== versionDetail?.forceUpdate;
		const isAppIdChanged = versionData.appId !== undefined && versionData.appId !== versionDetail?.appId;
		const isVersionChanged = versionData.version.trim().length > 0 && versionData.version !== versionDetail?.version;
		const isPlatformChanged = versionData.platform.trim().length > 0 && versionData.platform !== versionDetail?.platform;

		if (!isForceUpdateChanged && !isAppIdChanged && !isVersionChanged && !isPlatformChanged) {
			message.info("Değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		if (isVersionChanged && !isVersionValid(versionData.version)) {
			message.info("Lütfen geçerli bir versiyon giriniz. (Örn: 1.0.0)");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await MobileAppVersionService.updateMobileAppVersion(versionData);

			if (response.isSuccessful) {
				message.success("Mobil Uygulama Sürümü başarıyla güncellendi.");
				setVersionData(defaultMobileAppVersionData);
				setVersionDetail(null);
				navigate("/mobile-app-versions");
			} else {
				message.error(response.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [versionData, versionDetail, navigate]);

	const getMobileAppVersionDetails = useCallback(async (id: string) => { 
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await MobileAppVersionService.getMobileAppVersionDetails(id);

			if (response.isSuccessful) {
				setVersionDetail(response.result);
				setVersionData({
					...defaultMobileAppVersionData,
					versionId: response.result.versionId
				});
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		}finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const getMobileAppVersionList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await MobileAppVersionService.getMobileAppVersionList();

			if (response.isSuccessful) {
				setVersionList(response.result?.items || []);
				clearMobileAppVersionData();
			} else {
				message.error(response.error ? response.error : "Mobil Uygulama Sürümü listesi yüklenirken hata oluştu.");
				setVersionList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [clearMobileAppVersionData]);

	const removeMobileAppVersion = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await MobileAppVersionService.removeMobileAppVersion(id);
			if (response.isSuccessful) {
				message.success("Mobil Uygulama Sürümü başarıyla silindi.");
				await getMobileAppVersionList();
			} else {
				message.error(response.error?.message || "Banner silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [getMobileAppVersionList]);

	const contextValue = useMemo<MobileAppVersionContextState>(
		() => ({
			versionData,
			versionList,
			versionDetail,
			setVersionData,
			operationStatus,
			addMobileAppVersion,
			removeMobileAppVersion,
			updateMobileAppVersion,
			getMobileAppVersionList,
			getMobileAppVersionDetails,
			clearMobileAppVersionData,
			clearMobileAppVersionList
		}),
		[
			versionData,
			versionList,
			versionDetail,
			setVersionData,
			operationStatus,
			addMobileAppVersion,
			removeMobileAppVersion,
			updateMobileAppVersion,
			getMobileAppVersionList,
			getMobileAppVersionDetails,
			clearMobileAppVersionData,
			clearMobileAppVersionList
		]
	);

	return (
		<MobileAppVersionContext.Provider value={contextValue}>
			{children}
		</MobileAppVersionContext.Provider>
	);
};